import { useState, useCallback, useMemo, memo } from 'react';
import { Calendar, Select, IconButton } from 'components';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { getMonthsList, getYearsList, cloneDate } from 'utils';
import {
  getMonth,
  setMonth,
  setYear,
  addMonths,
  subMonths,
  isBefore,
  isAfter,
  isSameMonth,
} from 'date-fns';
import { useStyles } from './style';

interface Props {
  date: Date | null;
  handleChange: (date: Date | null) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  startFrom?: number;
  endIn?: number;
}

const monthsList = getMonthsList();

const DatePickerMenu: React.FC<Props> = ({
  date,
  handleChange,
  minDate,
  maxDate,
  startFrom = 1900,
  endIn = 2100,
}) => {
  const classes = useStyles();
  const monthesCls = useMemo(
    () => ({
      root: classes.selectRoot,
      value: classes.selectLabel,
      listItem: classes.listItem,
    }),
    [classes]
  );
  const yearCls = useMemo(
    () => ({
      value: classes.selectLabel,
    }),
    [classes]
  );

  const [currentDate, setCurrentDate] = useState(() => {
    return date ? cloneDate(date) : new Date();
  });

  const yearsList = useMemo(
    () => getYearsList(startFrom, endIn),
    [startFrom, endIn]
  );

  const lastDay = useMemo(
    () => new Date(Number(yearsList[yearsList.length - 1]), 11, 31, 0, 0, 0),
    [yearsList]
  );

  const firstDay = useMemo(
    () => new Date(Number(yearsList[0]), 0, 1, 0, 0, 0),
    [yearsList]
  );

  const selectMonth = useCallback((newMonth: string) => {
    const monthIdx = monthsList.findIndex((month) => month === newMonth);

    setCurrentDate((d) => setMonth(d, monthIdx));
  }, []);

  const selectYear = useCallback((newYear: string) => {
    const y = +newYear;

    if (typeof y === 'number' && !isNaN(y)) {
      setCurrentDate((d) => setYear(d, y));
    }
  }, []);

  const selectPreviousMonth = () => {
    setCurrentDate((d) => subMonths(d, 1));
  };

  const selectNextMonth = () => {
    setCurrentDate((d) => addMonths(d, 1));
  };

  const selectDay = useCallback(
    (day: Date) => {
      handleChange(day);
    },
    [handleChange]
  );

  const isDisabledPrevButton = () => {
    const d = subMonths(currentDate, 1);

    return (
      isBefore(d, firstDay) ||
      (minDate ? isBefore(d, minDate) && !isSameMonth(d, minDate) : false)
    );
  };

  const isDisabledNextButton = () => {
    const d = addMonths(currentDate, 1);

    return (
      isAfter(d, lastDay) ||
      (maxDate ? isAfter(d, maxDate) && !isSameMonth(d, maxDate) : false)
    );
  };

  const month = monthsList[getMonth(currentDate)];
  const year = String(currentDate.getFullYear());

  return (
    <>
      <div className={classes.wrapper}>
        <IconButton
          onClick={selectPreviousMonth}
          disabled={isDisabledPrevButton()}
        >
          <ChevronLeftIcon className={classes.arrowIcon} />
        </IconButton>
        <div className={classes.centered}>
          <div className={classes.rangeWrapper}>
            <Select
              value={month}
              options={monthsList}
              onSelect={selectMonth}
              cls={monthesCls}
              withButton={false}
            />
          </div>
          <div className={classes.rangeWrapper}>
            <Select
              value={year}
              options={yearsList}
              onSelect={selectYear}
              cls={yearCls}
              withButton={false}
            />
          </div>
        </div>
        <IconButton onClick={selectNextMonth} disabled={isDisabledNextButton()}>
          <ChevronRightIcon className={classes.arrowIcon} />
        </IconButton>
      </div>
      <Calendar
        activeDate={date || undefined}
        currentDate={currentDate}
        handleChangeDate={selectDay}
        minDate={minDate || firstDay}
        maxDate={maxDate || lastDay}
      />
    </>
  );
};

export default memo(DatePickerMenu);
